import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { ReactComponent as DownArrow } from '../../assets/images/icons/arrow_down.svg';
import { ReactComponent as UpArrow } from '../../assets/images/icons/arrow_up.svg';
import { Image } from '../Image';
import styles from './WhatsIncluded.module.scss';

export interface WhatsIncludedItem {
  imageUrl?: string;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  name: string;
  description?: string;
  zoomImage?: boolean;
}

export interface WhatsIncludedProps {
  items: WhatsIncludedItem[];
  defaultExpanded?: boolean;
}

/**
 * Expandable accordion showing items included in a cart bundle.
 * Used in CartItemV2 to display device, band, base, and warranty.
 */
export default function WhatsIncluded({ items, defaultExpanded = true }: WhatsIncludedProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);

  const toggleExpanded = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <div className={styles.whatsIncluded}>
      <button type="button" className={styles.toggleButton} onClick={toggleExpanded} aria-expanded={expanded}>
        <span className={styles.toggleText}>{t('cart_item_v2.whats_included')}</span>
        <span className={styles.toggleRight}>
          <span className={styles.itemCount}>{`(${items.length})`}</span>
          {expanded ? <UpArrow className={styles.arrow} /> : <DownArrow className={styles.arrow} />}
        </span>
      </button>

      <div className={classNames(styles.itemsContainer, { [styles.expanded]: expanded })} aria-hidden={!expanded}>
        {items.map((item, index) => (
          <div key={index} className={styles.item}>
            <div className={styles.itemImage}>
              {item.icon ? (
                <item.icon className={styles.icon} />
              ) : (
                <Image
                  className={classNames(styles.image, { [styles.zoomedImage]: item.zoomImage })}
                  image={{ type: 'image', url: item.imageUrl ?? '' }}
                  alt={item.name}
                />
              )}
            </div>
            <div className={styles.itemDetails}>
              <span className={styles.itemName}>{item.name}</span>
              {item.description && <span className={styles.itemDescription}>{item.description}</span>}
            </div>
            <span className={styles.itemPrice}>{t('cart_item_v2.free')}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
